import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import Toggle from '../ui/Toggle';
import {
  AI_PROVIDERS,
  PROVIDER_MODELS,
  DEFAULT_PROVIDER,
  DEFAULT_MODEL,
  saveAiAgent,
  type AiAgentDoc,
  type AiProvider,
  type KnowledgeSetDoc,
} from '../../lib/aiAgents';

interface AgentModalProps {
  open: boolean;
  agent?: AiAgentDoc | null;
  knowledgeSets: KnowledgeSetDoc[];
  onClose: () => void;
  onSaved: () => void;
}

export function AgentModal({ open, agent, knowledgeSets, onClose, onSaved }: AgentModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState<AiProvider>(DEFAULT_PROVIDER);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [knowledgeSetId, setKnowledgeSetId] = useState<string>('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(600);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (agent) {
      setName(agent.name);
      setSlug(agent.slug);
      setStatus(agent.status);
      setDescription(agent.description || '');
      setProvider(agent.provider);
      setModel(agent.model);
      setKnowledgeSetId(agent.knowledgeSetId || '');
      setSystemPrompt(agent.systemPrompt);
      setTemperature(agent.temperature ?? 0.7);
      setMaxTokens(agent.maxTokens ?? 600);
    } else {
      setName('');
      setSlug('');
      setStatus('active');
      setDescription('');
      setProvider(DEFAULT_PROVIDER);
      setModel(DEFAULT_MODEL);
      setKnowledgeSetId('');
      setSystemPrompt('You are a helpful assistant for this business. Answer customer questions politely and accurately.');
      setTemperature(0.7);
      setMaxTokens(600);
    }
    setError(null);
  }, [agent, open]);

  // Update model when provider changes if current model doesn't match provider
  const handleProviderChange = (newProvider: AiProvider) => {
    setProvider(newProvider);
    const available = PROVIDER_MODELS[newProvider];
    if (available && !available.includes(model)) {
      setModel(available[0]);
    }
  };

  const handleNameChange = (val: string) => {
    setName(val);
    if (!agent) {
      const generated = val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      setSlug(generated);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('ai.agents.nameRequired'));
      return;
    }
    if (!slug.trim()) {
      setError(t('ai.agents.slugRequired'));
      return;
    }
    if (!systemPrompt.trim()) {
      setError(t('ai.agents.promptRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await saveAiAgent({
        id: agent?.id,
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        status,
        description: description.trim() || undefined,
        provider,
        model,
        knowledgeSetId: knowledgeSetId.trim() || null,
        systemPrompt: systemPrompt.trim(),
        temperature,
        maxTokens,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={agent ? t('ai.agents.edit') : t('ai.agents.create')}
    >
      <form onSubmit={handleSave} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.agents.name')} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={t('ai.agent.namePlaceholder') || 'e.g. Sales Specialist'}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.agents.slug')} *
            </label>
            <div className="relative mt-1">
              <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-xs text-ink-3">@</span>
              <input
                type="text"
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                placeholder="sales-bot"
                className="w-full rounded-lg border border-line bg-surface py-2 pe-3 ps-7 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
            <span className="mt-1 block text-[11px] text-ink-3 dark:text-slate-400">
              {t('ai.agents.slugHint')}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-line p-3 dark:border-slate-700">
          <div>
            <span className="block text-xs font-semibold text-ink dark:text-white">
              {t('ai.agents.status')}
            </span>
            <span className="text-[11px] text-ink-3 dark:text-slate-400">
              {status === 'active' ? t('ai.agents.active') : t('ai.agents.inactive')}
            </span>
          </div>
          <Toggle
            on={status === 'active'}
            onChange={(val) => setStatus(val ? 'active' : 'inactive')}
            label={t('ai.agents.status')}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('ai.agents.description')}
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief purpose of this agent"
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.agents.provider')}
            </label>
            <select
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value as AiProvider)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              {AI_PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.agents.model')}
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              {PROVIDER_MODELS[provider]?.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('ai.agents.knowledgeSet')}
          </label>
          <select
            value={knowledgeSetId}
            onChange={(e) => setKnowledgeSetId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('ai.agents.noKnowledgeSet')}</option>
            {knowledgeSets.map((ks) => (
              <option key={ks.id} value={ks.id}>
                {ks.name} ({ks.totalChunks || 0} chunks)
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.agents.systemPrompt')} *
            </label>
            <span className="text-[11px] text-ink-3 dark:text-slate-400">
              {systemPrompt.length}/10000
            </span>
          </div>
          <textarea
            rows={5}
            required
            maxLength={10000}
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="Define the agent persona, role, business guidelines, and how it should answer customer queries."
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
          <span className="mt-1 block text-[11px] text-ink-3 dark:text-slate-400">
            {t('ai.agents.systemPromptHint')}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                {t('ai.agents.temperature')}
              </label>
              <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs font-bold text-brand dark:bg-brand-950/50 dark:text-brand-tint">
                {temperature}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="mt-2 w-full accent-brand"
            />
            <span className="block text-[11px] text-ink-3 dark:text-slate-400">
              {t('ai.agents.temperatureHint')}
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.agents.maxTokens')}
            </label>
            <input
              type="number"
              min="50"
              max="4000"
              step="50"
              value={maxTokens}
              onChange={(e) => setMaxTokens(parseInt(e.target.value, 10) || 600)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            <span className="mt-1 block text-[11px] text-ink-3 dark:text-slate-400">
              50 - 4000 tokens
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
